import L from "leaflet";
import "leaflet/dist/leaflet.css";

import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";

delete L.Icon.Default.prototype._getIconUrl;

L.Icon.Default.mergeOptions({
    iconRetinaUrl: markerIcon2x,
    iconUrl: markerIcon,
    shadowUrl: markerShadow
});

function isMobile() {
    return window.matchMedia("(max-width: 767.98px)").matches;
}

function getTenantMap(tenants) {
    return new Map(tenants.map(t => [t.name, t]));
}

function getBuildingMap(buildings) {
    return new Map(buildings.map(b => [b.name, b]));
}

function buildPopupHtml(building, tenantMap) {
    let html = "";

    const displayName = building.displayName || building.name;

    if (building.url) {
        html += `<a href="${building.url}" target="_blank" rel="noopener"><strong>${displayName}</strong></a>`;
    } else {
        html += `<strong>${displayName}</strong>`;
    }

    if (building.address) {
        html += `<br><div>${building.address}</div>`;
    }

    if (building.phone) {
        html += `<div>${building.phone}</div>`;
    }

    if (building.tenants?.length) {
        html += `<br><strong>Tenants</strong>`;

        building.tenants.forEach(tenantName => {
            const tenant = tenantMap.get(tenantName);
            if (!tenant) return;

            html += `<div class="mt-2">`;

            if (tenant.url) {
                html += `<a href="${tenant.url}" target="_blank" rel="noopener">${tenant.name}</a>`;
            } else {
                html += `${tenant.name}`;
            }

            if (tenant.address) {
                html += `<br>${tenant.address}`;
            }

            if (tenant.phone) {
                html += `<br>${tenant.phone}`;
            }

            html += `</div>`;
        });
    }

    return html;
}

function createDirectoryLayer(directory, buildingMap, tenantMap, options = {}) {
    const markers = [];

    directory.buildings.forEach(buildingName => {
        const building = buildingMap.get(buildingName);
        if (!building) return;

        const marker = L.marker(building.coordinate);

        const tooltipText = building.displayName || building.name;

        if (options.showPermanentTooltips) {
            marker.bindTooltip(tooltipText, {
                permanent: true,
                direction: "top",
                offset: [-13, -8]
            }).openTooltip();
        } else {
            marker.bindTooltip(tooltipText);
        }

        marker.bindPopup(buildPopupHtml(building, tenantMap));
        markers.push(marker);
    });

    return L.layerGroup(markers);
}

function createMap(mapSelector, options = {}) {
    const mapEl =
        typeof mapSelector === "string"
            ? document.querySelector(mapSelector)
            : mapSelector;

    if (!mapEl) return null;

    const map = L.map(mapEl, {
        minZoom: options.minZoom ?? 10,
        maxZoom: options.maxZoom ?? 19,
        scrollWheelZoom: options.scrollWheelZoom ?? false
    }).setView(options.center ?? [45.5494, -122.39983], options.zoom ?? 15);

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: "&copy; OpenStreetMap contributors"
    }).addTo(map);

    if (options.tileUrl) {
        L.tileLayer(options.tileUrl, {
            attribution: options.attribution ?? '© <a href="http://www.portofportland.com">Port of Portland</a>',
            tms: options.tms ?? false,
            opacity: options.opacity ?? 1
        }).addTo(map);
    }

    return map;
}

function initTtdDirectoryMap(mapSelector, config) {
    const {
        center = [45.5494, -122.39983],
        zoom = 15,
        minZoom = 10,
        maxZoom = 19,
        showPermanentTooltips = true,
        scrollWheelZoom = false,
        tenants = [],
        buildings = [],
        directories = [],
        tileUrl = null
    } = config;

    const map = createMap(mapSelector, {
        center,
        zoom,
        minZoom,
        maxZoom,
        scrollWheelZoom,
        tileUrl
    });

    if (!map) return null;

    const buildingMap = getBuildingMap(buildings);
    const tenantMap = getTenantMap(tenants);

    const overlays = {};
    let firstLayer = null;

    directories.forEach(directory => {
        const layer = createDirectoryLayer(directory, buildingMap, tenantMap, {
            showPermanentTooltips
        });

        overlays[directory.name] = layer;

        if (!firstLayer) {
            firstLayer = layer;
        }
    });

    if (firstLayer) {
        firstLayer.addTo(map);
    }

    L.control.layers(null, overlays, {
        collapsed: isMobile()
    }).addTo(map);

    return map;
}

window.PortJS = window.PortJS || {};
window.PortJS.initTtdDirectoryMap = initTtdDirectoryMap;

export { initTtdDirectoryMap };